export type LogSeverity = 'error' | 'warning' | null;

export type IssueWindow = 'day' | 'week' | 'all';

export const ISSUE_WINDOWS: { value: IssueWindow; label: string }[] = [
    { value: 'day', label: 'Last 24 hours' },
    { value: 'week', label: 'Last 7 days' },
    { value: 'all', label: 'Entire log' },
];

const WINDOW_MS: Record<IssueWindow, number | null> = {
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    all: null,
};

// Known harmless messages that contain error/warning words. Extend when a log produces false positives.
const BENIGN_PATTERNS: RegExp[] = [
    /service not found or error:/i,
    /\bno (errors?|warnings?)\b/i,
    /\b0 (errors?|warnings?|failed|failures?)\b/i,
    /\b(errors?|warnings?|failed|failures?)\s*[:=]\s*0\b/i,
    /-ErrorAction\b/i,
    /\btimeout set to never\b/i,
];

const EXPLICIT_ERROR = /(^|[\s[|(])(ERROR|ERR|FATAL|CRITICAL|CRIT)(\]|\)|:|\s\|)/i;
const EXPLICIT_WARNING = /(^|[\s[|(])(WARNING|WARN|WRN)(\]|\)|:|\s\|)/i;
const EXPLICIT_INFO = /(^|[\s[|(])(INFO|INF|DEBUG|DBG|TRACE|TRC|OK)(\]|\)|:|\s\|)/i;
const DOTNET_ERROR = /^\s*(fail|crit):/;
const DOTNET_WARNING = /^\s*warn:/;

const ERROR_KEYWORDS: RegExp[] = [
    /\b(failed|failure|fatal|unhandled)\b/i,
    /\berrors?\b(?!\.log)/i,
    /\w+Exception\b/,
    /\b(CategoryInfo|FullyQualifiedErrorId)\b/,
];
const WARNING_KEYWORDS = /\b(warn|warning|warnings|timed out|timeout|retry|retrying)\b/i;
const EXIT_CODE = /exit\s*code\D{0,3}(-?\d+)/i;

export const classifyLogLine = (line: string): LogSeverity => {
    if (!line.trim() || BENIGN_PATTERNS.some((pattern) => pattern.test(line))) return null;
    if (EXPLICIT_ERROR.test(line) || DOTNET_ERROR.test(line)) return 'error';
    if (EXPLICIT_WARNING.test(line) || DOTNET_WARNING.test(line)) return 'warning';
    if (EXPLICIT_INFO.test(line)) return null;

    const exitCode = EXIT_CODE.exec(line);
    if (exitCode && Number(exitCode[1]) !== 0) return 'error';
    if (ERROR_KEYWORDS.some((pattern) => pattern.test(line))) return 'error';
    if (WARNING_KEYWORDS.test(line)) return 'warning';
    return null;
};

const ISO_TIMESTAMP = /^\W{0,3}(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;
const DAY_FIRST_TIMESTAMP = /^\W{0,3}(\d{2})[-/.](\d{2})[-/.](\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;

/** Reads a leading timestamp as device-local time; returns null when the line has none. */
export const parseLineTimestamp = (line: string): number | null => {
    let parts: number[] | null = null;
    const iso = ISO_TIMESTAMP.exec(line);
    if (iso) {
        parts = [iso[1], iso[2], iso[3], iso[4], iso[5], iso[6] ?? '0'].map(Number);
    } else {
        const dayFirst = DAY_FIRST_TIMESTAMP.exec(line);
        if (dayFirst) {
            parts = [dayFirst[3], dayFirst[2], dayFirst[1], dayFirst[4], dayFirst[5], dayFirst[6] ?? '0'].map(Number);
        }
    }
    if (!parts) return null;
    const [year, month, day, hour, minute, second] = parts;
    if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59 || second > 59) return null;
    return new Date(year, month - 1, day, hour, minute, second).getTime();
};

export interface AnalyzedLogLine {
    text: string;
    severity: LogSeverity;
    /** False when the line is older than the selected window; such issues are shown dimmed and not counted. */
    inWindow: boolean;
}

export interface LogAnalysis {
    lines: AnalyzedLogLine[];
    /** Line indexes of counted (in-window) errors and warnings, in file order. */
    issueLines: number[];
    errorCount: number;
    warningCount: number;
    olderIssueCount: number;
}

export const analyzeLog = (content: string | null | undefined, window: IssueWindow, referenceTime: number): LogAnalysis => {
    const windowMs = WINDOW_MS[window];
    const cutoff = windowMs == null || Number.isNaN(referenceTime) ? null : referenceTime - windowMs;
    const rawLines = content ? content.split(/\r?\n/) : [];
    if (rawLines.length > 1 && rawLines[rawLines.length - 1] === '') rawLines.pop();

    const result: LogAnalysis = { lines: [], issueLines: [], errorCount: 0, warningCount: 0, olderIssueCount: 0 };
    // Continuation lines (stack traces, PowerShell error records) inherit the previous timestamp.
    let currentTimestamp: number | null = null;
    rawLines.forEach((text, index) => {
        currentTimestamp = parseLineTimestamp(text) ?? currentTimestamp;
        const severity = classifyLogLine(text);
        const inWindow = cutoff == null || currentTimestamp == null || currentTimestamp >= cutoff;
        result.lines.push({ text, severity, inWindow });
        if (!severity) return;
        if (!inWindow) {
            result.olderIssueCount += 1;
            return;
        }
        result.issueLines.push(index);
        if (severity === 'error') result.errorCount += 1;
        else result.warningCount += 1;
    });
    return result;
};

/** Returns the line indexes to show when filtering on issues, with -1 marking skipped ranges. */
export const issueContextLines = (analysis: LogAnalysis, context = 2): number[] => {
    const visible = new Set<number>();
    analysis.issueLines.forEach((line) => {
        for (let index = Math.max(0, line - context); index <= Math.min(analysis.lines.length - 1, line + context); index += 1) {
            visible.add(index);
        }
    });
    const sorted = Array.from(visible).sort((a, b) => a - b);
    const output: number[] = [];
    sorted.forEach((line, position) => {
        const previous = position === 0 ? -1 : sorted[position - 1];
        if (line !== previous + 1) output.push(-1);
        output.push(line);
    });
    if (sorted.length && sorted[sorted.length - 1] < analysis.lines.length - 1) output.push(-1);
    return output;
};
